import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { cn } from '@heidi/utils';
import { type ReactNode } from 'react';
import {
  InlineApprovalActions,
  type InlineApprovalActionsProps,
} from '../../../inline-approval/inline-approval-actions';

const noop = () => undefined;

/**
 * Slim single-row HITL approval banner — the compact sibling of
 * ``InlineApprovalCardShell``. A brand mark + a one-line "Heidi wants to
 * …" prompt + the shared Cancel / Approve row, all on one line. Any preview
 * of what's being approved is rendered as a separate block above the banner
 * by the caller, not nested inside it.
 */
export interface InlineApprovalBannerProps extends Partial<
  Pick<
    InlineApprovalActionsProps,
    'onApprove' | 'onDeny' | 'pendingVerdict' | 'disabled' | 'denyLabel' | 'approveLabel'
  >
> {
  /** One-line prompt, e.g. "Heidi wants to create 2 clinical tasks". */
  title: ReactNode;
  /** Attribute on the root for test selectors / DOM observers. */
  'data-testid'?: string;
  /**
   * Replaces the default Cancel / Approve row (e.g. OAuth Approve that is
   * itself a connect button). When omitted, ``onApprove`` / ``onDeny`` drive
   * the shared action row.
   */
  actions?: ReactNode;
  /** Leading mark; defaults to the Heidi brand icon. */
  mark?: ReactNode;
}

export const InlineApprovalBanner = ({
  title,
  onApprove,
  onDeny,
  pendingVerdict,
  disabled = false,
  denyLabel,
  approveLabel,
  actions,
  mark,
  'data-testid': dataTestId,
}: InlineApprovalBannerProps) => {
  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-testid={dataTestId}
    >
      {mark ?? <HeidiLogoMark className="size-5" />}
      <p className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-text-primary [overflow-wrap:anywhere]">
        {title}
      </p>
      <div className="shrink-0">
        {actions ?? (
          <InlineApprovalActions
            onApprove={onApprove ?? noop}
            onDeny={onDeny ?? noop}
            pendingVerdict={pendingVerdict ?? null}
            disabled={disabled}
            denyLabel={denyLabel}
            approveLabel={approveLabel}
          />
        )}
      </div>
    </div>
  );
};
